/**
 * TODO: Properly remove @transfeeradev/redis dependency by not using dependencies that use it.
 *
 * This non-sense below is a temporary solution to avoid runtime errors while being able
 * to remove server-side Redis packages (ioredis) from the browser bundle.
 *
 * This because @transfeeradev/transfer-validation pulls in @transfeeradev/redis which
 * depends on ioredis — a Node.js-only package that requires built-ins unavailable in
 * the browser (crypto, stream, net, tls, dns, fs, etc.).
 *
 * Any unexpected call to these Redis methods will be captured by Sentry so that we
 * can track and properly fix it.
 */
import * as Sentry from '@sentry/browser';

const logSentry = logMessage => {
  Sentry.captureException(new Error(`[WEBAPP REDIS] - ${logMessage}`), {
    level: Sentry.Severity.Info,
    fingerprint: ['WEBAPP REDIS']
  });
};

const mockRedisMethod = (logMessage, returnValue = null) => {
  return async () => {
    logSentry(logMessage);
    return returnValue;
  };
};

export class RedisError extends Error {}
export class RedisNotInitializedError extends RedisError {}
export class RedisAlreadyLocked extends RedisError {}
export class RedisNotLocked extends RedisError {}

export class RedisLock {
  async isLocked() {
    logSentry('RedisLock.isLocked');
    return false;
  }

  async release() {
    logSentry('RedisLock.release');
  }

  async ttl() {
    logSentry('RedisLock.ttl');
    return -1;
  }

  async extend() {
    logSentry('RedisLock.extend');
  }
}

export class RedisSemaphore {
  async isLocked() {
    logSentry('RedisSemaphore.isLocked');
    return false;
  }

  async release() {
    logSentry('RedisSemaphore.release');
  }
}

export class RedisClient {
  constructor() {
    logSentry('RedisClient.constructor');
  }

  connect = mockRedisMethod('RedisClient.connect', this);
  redis = () => {
    logSentry('RedisClient.redis');
    return {};
  };

  getRaw = mockRedisMethod('RedisClient.getRaw', null);
  setRaw = mockRedisMethod('RedisClient.setRaw', null);
  set = mockRedisMethod('RedisClient.set', null);
  get = mockRedisMethod('RedisClient.get', null);
  remove = mockRedisMethod('RedisClient.remove', null);
  ttl = mockRedisMethod('RedisClient.ttl', -1);
  expire = mockRedisMethod('RedisClient.expire', null);
  exists = mockRedisMethod('RedisClient.exists', 0);
  copy = mockRedisMethod('RedisClient.copy', null);
  find = mockRedisMethod('RedisClient.find', []);
  getAll = mockRedisMethod('RedisClient.getAll', []);
  findValues = mockRedisMethod('RedisClient.findValues', []);
  lock = mockRedisMethod('RedisClient.lock', new RedisLock());
  releaseLock = mockRedisMethod('RedisClient.releaseLock', null);
  isLocked = mockRedisMethod('RedisClient.isLocked', false);
  extendLock = mockRedisMethod('RedisClient.extendLock', null);
  acquireSemaphore = mockRedisMethod('RedisClient.acquireSemaphore', new RedisSemaphore());
  releaseSemaphore = mockRedisMethod('RedisClient.releaseSemaphore', true);
  isSemaphoreLocked = mockRedisMethod('RedisClient.isSemaphoreLocked', false);
  lockRWLockWriter = mockRedisMethod('RedisClient.lockRWLockWriter', new RedisLock());
  lockRWLockReader = mockRedisMethod('RedisClient.lockRWLockReader', new RedisSemaphore());
  purge = mockRedisMethod('RedisClient.purge', null);
  incr = mockRedisMethod('RedisClient.incr', 0);
  decr = mockRedisMethod('RedisClient.decr', 0);
  close = mockRedisMethod('RedisClient.close', null);

  key(key) {
    return key;
  }

  sanitizeKey(key) {
    return key;
  }

  packData(value) {
    return JSON.stringify({ data: value });
  }

  unpackData(stored) {
    try {
      const parsed = JSON.parse(stored);
      return parsed?.data ?? null;
    } catch {
      return null;
    }
  }
}

export const RedisFactory = async () => {
  logSentry('RedisFactory');
  return { redis: {}, isCluster: false };
};

export const constants = {};
export const hapi = {};

export default {
  RedisClient,
  RedisFactory,
  RedisLock,
  RedisSemaphore,
  RedisError,
  RedisNotInitializedError,
  RedisAlreadyLocked,
  RedisNotLocked,
  constants,
  hapi
};
